/**
 * Settings > Customer Surveys (tracker item 69): turn automatic surveys on
 * or off, how long after a completed job the customer is asked, what counts
 * as a low score (it raises an alert for the office), how often one customer
 * may be asked, and the line above the comment box. ADMIN edits; an office
 * manager can read. The wording of the email is edited in Settings >
 * Notifications.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, ISurveysApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManageSurveys, canViewSurveys } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { NOTIFICATION_SETTINGS_PATH } from './notificationUi';
import { SURVEYS_PATH, SURVEY_TEMPLATE_PATH, SATISFACTION_REPORT_PATH } from '../surveys/surveyUi';
import { STAFF_ALERT_PREFERENCES_PATH } from '../../utils/staffNotifications';

interface Form {
  on: boolean;
  delayHours: string;
  lowScoreThreshold: string;
  customerCooldownDays: string;
  commentPrompt: string;
}

function formOf(settings: types.SurveySettings): Form {
  return {
    on: settings.mode === 'ON',
    delayHours: String(settings.delayHours),
    lowScoreThreshold: String(settings.lowScoreThreshold),
    customerCooldownDays: String(settings.customerCooldownDays),
    commentPrompt: settings.commentPrompt ?? '',
  };
}

/** The request body for the fields that differ from the saved settings; field errors are returned as text. */
export function surveySettingsChanges(form: Form, saved: types.SurveySettings): { input: types.UpdateSurveySettingsInput; error: string | null } {
  const input: types.UpdateSurveySettingsInput = {};
  const whole = (value: string, name: string, min: number, max: number): number | string => {
    const n = Number(value);
    return value.trim() !== '' && Number.isInteger(n) && n >= min && n <= max ? n : `${name}: enter a whole number from ${min} to ${max}.`;
  };
  const delay = whole(form.delayHours, 'Hours to wait', 0, 720);
  const cooldown = whole(form.customerCooldownDays, 'Days between surveys to one customer', 0, 365);
  const threshold = whole(form.lowScoreThreshold, 'Low score', 1, 4);
  for (const value of [delay, cooldown, threshold]) if (typeof value === 'string') return { input, error: value };
  if (form.commentPrompt.trim().length > 300) return { input, error: 'The comment question can be at most 300 characters.' };
  const mode: types.SurveyMode = form.on ? 'ON' : 'OFF';
  if (mode !== saved.mode) input.mode = mode;
  if (delay !== saved.delayHours) input.delayHours = delay as number;
  if (threshold !== saved.lowScoreThreshold) input.lowScoreThreshold = threshold as number;
  if (cooldown !== saved.customerCooldownDays) input.customerCooldownDays = cooldown as number;
  if ((form.commentPrompt.trim() || null) !== saved.commentPrompt) input.commentPrompt = form.commentPrompt.trim() || null;
  return { input, error: null };
}

export function SurveySettingsPage() {
  const { user } = useAuth();
  const api = useMemo<ISurveysApi>(() => createAPIClient(), []);
  const allowed = canViewSurveys(user);
  const canEdit = canManageSurveys(user);
  const [saved, setSaved] = useState<types.SurveySettings | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    api.getSurveySettings()
      .then((settings) => { if (active) { setSaved(settings); setForm(formOf(settings)); } })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, 'Could not load the survey settings')); });
    return () => { active = false; };
  }, [api, allowed]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">Survey settings are available to admins and office managers.</p>;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!saved || !form || !canEdit) return;
    const { input, error: problem } = surveySettingsChanges(form, saved);
    setNotice(null);
    if (problem) { setError(problem); return; }
    if (Object.keys(input).length === 0) { setError(null); setNotice('Nothing to save: no settings were changed.'); return; }
    setBusy(true);
    setError(null);
    try {
      const next = await api.updateSurveySettings(input);
      setSaved(next);
      setForm(formOf(next));
      setNotice('Survey settings saved.');
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not save the survey settings'));
    } finally {
      setBusy(false);
    }
  }

  const update = (patch: Partial<Form>) => setForm((current) => (current ? { ...current, ...patch } : current));
  const scale = saved?.scale.labels ?? {};

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl font-bold text-gray-900">Customer surveys</h1>
      <p className="mt-1 text-sm text-gray-600">
        After a job is completed, email the customer a link to rate the visit from 1 to 5 stars and leave a comment. Answers show on the job, in the{' '}
        <Link to={SURVEYS_PATH} className="text-blue-700 underline">survey inbox</Link> and in the{' '}
        <Link to={SATISFACTION_REPORT_PATH} className="text-blue-700 underline">satisfaction report</Link>.
      </p>
      {error && <div className="mt-4"><Toast kind="error">{error}</Toast></div>}
      {notice && <div className="mt-4"><Toast kind="success">{notice}</Toast></div>}
      {!saved || !form ? (
        !error && <p className="mt-6 text-sm text-gray-500">Loading…</p>
      ) : (
        <form onSubmit={save} className="mt-6 space-y-6 rounded-lg bg-white p-5 shadow sm:p-7" noValidate>
          {!canEdit && <p role="note" className="rounded bg-gray-50 p-3 text-sm text-gray-700">Only an admin can change these settings.</p>}
          {!saved.notificationsEnabled && (
            <p role="note" className="rounded bg-amber-50 p-3 text-sm text-amber-900">
              Notifications are turned off for this company, so no survey email goes out yet.{' '}
              {canEdit && <Link to={NOTIFICATION_SETTINGS_PATH} className="underline">Settings &gt; Notifications</Link>}
            </p>
          )}
          {saved.mode === 'ON' && saved.notificationsEnabled && !saved.eventActive && (
            <p role="note" className="rounded bg-amber-50 p-3 text-sm text-amber-900">The survey message is turned off, so surveys wait until it is turned back on.</p>
          )}

          <div className="flex items-start gap-3">
            <input id="survey-on" type="checkbox" className="mt-1 h-5 w-5" checked={form.on} disabled={!canEdit || busy} onChange={(e) => update({ on: e.target.checked })} />
            <div>
              <label htmlFor="survey-on" className="font-medium text-gray-900">Ask customers automatically after a completed job</label>
              <p className="text-sm text-gray-600">
                Only jobs completed after you turn this on are included{saved.activeSince ? ` (since ${saved.activeSince})` : ''}. Customers who opted out, have no email address or whose address bounced are never asked. Flat-contract visits are not surveyed automatically. You can still send a survey by hand from any completed job.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="survey-delay" className={labelClass}>Hours to wait after completion</label>
              <input id="survey-delay" className={inputClass} inputMode="numeric" value={form.delayHours} disabled={!canEdit || busy} onChange={(e) => update({ delayHours: e.target.value })} />
              <p className="mt-1 text-xs text-gray-500">0 to 720. The survey goes out in the next worker run after this time.</p>
            </div>
            <div>
              <label htmlFor="survey-cooldown" className={labelClass}>Days between surveys to one customer</label>
              <input id="survey-cooldown" className={inputClass} inputMode="numeric" value={form.customerCooldownDays} disabled={!canEdit || busy} onChange={(e) => update({ customerCooldownDays: e.target.value })} />
              <p className="mt-1 text-xs text-gray-500">0 asks after every job; 30 asks a customer at most once a month.</p>
            </div>
            <div>
              <label htmlFor="survey-threshold" className={labelClass}>Low score</label>
              <select id="survey-threshold" className={inputClass} value={form.lowScoreThreshold} disabled={!canEdit || busy} onChange={(e) => update({ lowScoreThreshold: e.target.value })}>
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n === 1 ? `${n} star (${scale[1] ?? 'Poor'}) only` : `${n} stars or fewer`}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                A score at or below this raises a “Low survey score” alert for the office. Turn the alert on or off for yourself in <Link to={STAFF_ALERT_PREFERENCES_PATH} className="text-blue-700 underline">My Alerts</Link>.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="survey-prompt" className={labelClass}>Question above the comment box</label>
              <input id="survey-prompt" className={inputClass} maxLength={300} placeholder="Anything you would like to tell us? (optional)" value={form.commentPrompt} disabled={!canEdit || busy} onChange={(e) => update({ commentPrompt: e.target.value })} />
              <p className="mt-1 text-xs text-gray-500">Leave blank for the standard question.</p>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            The wording of the email is edited in{' '}
            {canEdit ? <Link to={SURVEY_TEMPLATE_PATH} className="text-blue-700 underline">Settings &gt; Notifications &gt; Customer survey</Link> : 'Settings > Notifications > Customer survey (admins)'}.
          </p>

          {canEdit && (
            <div className="flex justify-end">
              <button type="submit" className={primaryButtonClass} disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
            </div>
          )}
        </form>
      )}
    </main>
  );
}
